<template>
  <div v-if="isB2BPurchasable" class="primary-navigation__cart siteMenu__button">
    <button
      id="minicartTrigger__button"
      v-tippy="{
        html: '.js-miniCartTooltip',
        interactive: true,
        reactive: true,
        trigger: 'click',
        placement: 'bottom-end',
        flip: true,
        sticky: true,
        popperOptions: {
          positionFixed: true,
          modifiers: {
            keepTogether: true,
            preventOverflow: {
              enabled: false,
            },
            hide: {
              enabled: false,
            },
          },
        },
      }"
      class="header-bottom__cart-button menu-trigger flex"
      :title="bp('cartPage')"
      data-ga-cart="cart"
      :data-ga-label="totalItems"
    >
      <i class="md-24">shopping_cart</i>
      <span
        v-show="totalItems > 0"
        id="miniCartItemCount"
        class="header-bottom__cart-qty text-white"
        v-html="totalItems"
      />
    </button>

    <!-- Mini Cart Tooltip (Dropdown) -->
    <div v-cloak id="miniCartTooltip" class="b2b-mini-cart js-miniCartTooltip overflow-y-auto">
      <div v-if="isInContainerConfigMode">
        <ContainerConfiguration
          :base-properties="baseProperties"
          :containers="cart.containers"
          @close="isInContainerConfigMode = false"
        />
      </div>
      <div v-else>
        <!-- Content -->
        <div v-if="isCartEmpty">
          <heading id="miniCart__callout--empty" class="mb-3" :level="1" size="sm">
            {{ bp('popupCartEmpty') }}
          </heading>

          <div v-if="hasContainers">
            <drop-ship-orders-container
              :base-properties="baseProperties"
              :containers="cart.containers"
              :size="containerPageSize"
              :is-mini-cart="true"
              @add-container="isInContainerConfigMode = true"
            />
          </div>
        </div>

        <div v-else>
          <div v-if="hasContainers">
            <drop-ship-orders-container
              :base-properties="baseProperties"
              :containers="cart.containers"
              :size="containerPageSize"
              :is-mini-cart="true"
              @add-container="isInContainerConfigMode = true"
            />
          </div>

          <div v-if="rollupItems" id="miniCart__itemList--combined">
            <div v-if="bikes && bikes.qty" class="b2b-mini-cart__item flex justify-between items-start">
              <heading :level="1" size="sm">
                {{ bp('bikes') }}
              </heading>
              <div class="text-right text-md">
                <div class="mb-1">
                  {{ bp('qty') }}:
                  <span qaid="miniCartItem--bikes__quantity" v-html="bikes.qty" />
                </div>
                <div>
                  {{ bp('total') }}:
                  <span qaid="miniCartItem--bikes__total" v-html="bikes.price" />
                </div>
              </div>
            </div>
            <div v-if="parts && parts.qty" class="b2b-mini-cart__item flex justify-between items-start">
              <heading :level="1" size="sm">
                {{ bp('accessories') }}
              </heading>
              <div class="text-right text-md">
                <div class="mb-1">
                  {{ bp('qty') }}:
                  <span qaid="miniCartItem--parts__quantity" v-html="parts.qty" />
                </div>
                <div v-if="minDollarAmountPresent">
                  {{ bp('total') }}:
                  <span :class="{'text-warning': highlightPartsMinAmount}">{{ cart.parts.price }}</span>
                  / {{ cart.parts.vendorMinimumDollarAmount }}
                  {{ bp('minOrderValue') }}
                </div>
                <div v-else>
                  {{ bp('total') }}:
                  <span qaid="miniCartItem--parts__total" v-html="parts.price" />
                </div>
              </div>
            </div>
          </div>

          <div v-else id="miniCart__itemList">
            <div v-for="(item, key) in items" :key="key" class="b2b-mini-cart__item">
              <heading class="text-base font-body font-medium mb-3" :level="1" v-html="item.displayName" />

              <div class="media-object">
                <div class="media-object__section">
                  <div>
                    <img :src="fullImageURL(item)" qaid="miniCartItem__thumbnail" />
                  </div>
                </div>
                <div class="media-object__section text-md mt-1 ml-1">
                  <div class="mb-1">
                    {{ bp('qty') }}:
                    <span qaid="miniCartItem__quantity" v-html="item.qty" />
                  </div>
                  <div v-if="!consumerFriendlyMode">
                    {{ bp('total') }}:
                    <span qaid="miniCartItem__total" v-html="item.price" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!consumerFriendlyMode" class="b2b-mini-cart__total font-medium">
            {{ bp('total') }}
            <span id="miniCart__total" class="font-bold" v-html="cart.price" />
          </div>
        </div>

        <!-- Footer/Buttons -->

        <!-- Empty cart buttons -->
        <div v-if="isCartEmpty">
          <!-- Empty cart with allocated items -->
          <div v-if="hasAllocatedItems" class="buttons mt-3">
            <trek-link
              id="miniCart__allocatedItems"
              button
              secondary
              block
              :href="`${encodedContextPath}/checkout/multi/allocated-items`"
              ><span v-html="bp('itemsAllocated')"></span
            ></trek-link>

            <trek-link
              id="miniCart__bulkUploadBtn"
              button
              secondary
              block
              :href="`${encodedContextPath}/import/csv/saved-cart`"
              ><span v-html="bp('upload')"></span
            ></trek-link>

            <trek-link
              id="miniCart__checkoutBtn"
              button
              primary
              block
              :href="`${encodedContextPath}/checkout/multi/allocated-items`"
              :disabled="!isCheckoutEnabled"
              @click="checkoutDisabledCheck"
              ><span v-html="bp('checkout')"></span
            ></trek-link>
          </div>

          <!-- Empty cart without allocated items -->
          <trek-link
            v-else
            id="miniCart__bulkOrderUploadBtn"
            button
            primary
            block
            :href="`${encodedContextPath}/import/csv/saved-cart`"
            class="mb-0"
            ><span v-html="bp('upload')"></span
          ></trek-link>
        </div>

        <!-- Cart with items buttons -->
        <div v-else class="buttons mt-3">
          <trek-link id="miniCart__ViewCartBtn" button secondary block :href="`${encodedContextPath}/cart`"
            ><span v-html="bp('view')"></span
          ></trek-link>

          <trek-link
            id="miniCart__checkoutBtn"
            button
            primary
            block
            :href="`${encodedContextPath}/checkout/multi/checkout`"
            :disabled="!isCheckoutEnabled"
            @click="checkoutDisabledCheck"
            ><span v-html="bp('checkout')"></span
          ></trek-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import {mapState, mapActions, mapGetters} from 'vuex';
import TrekLink from '@/components/TrekLink';

import Heading from '@/components/Heading';
import DropShipOrdersContainer from '@/components/containers/drop-ship/DropShipOrdersContainer';
import ContainerConfiguration from '@/components/containers/drop-ship/ContainerConfiguration';

export default {
  components: {Heading, DropShipOrdersContainer, ContainerConfiguration, TrekLink},

  props: {
    baseProperties: {
      type: Array,
      required: false,
      default: () => [],
    },
    containerPageSize: {
      type: Number,
      required: false,
      default: 4,
    },
  },

  data() {
    return {
      isInContainerConfigMode: false,
    };
  },

  computed: {
    totalItems() {
      if (isEmpty(this.items)) return this.items.length;

      return this.items.reduce((totalQty, item) => totalQty + item.qty, 0);
    },

    isCartEmpty() {
      return !(this.items && this.items.length);
    },

    hasAllocatedItems() {
      return this.cart && this.cart.allocated;
    },

    rollupItems() {
      return this.items && this.items.length > 3;
    },

    hasContainers() {
      return this.cart && this.cart.containers !== null;
    },

    isCheckoutEnabled() {
      return this.cart && (this.cart.isCheckoutAllowed || this.cart.allocated);
    },

    highlightPartsMinAmount() {
      if (this.cart && this.cart.parts && this.cart.parts.price && this.cart.parts.vendorMinimumDollarAmount) {
        // Highlight total price if it's less than the minimum order amount
        return (
          Number(this.cart.parts.price.replace(/[^0-9.-]+/g, '')) <
          Number(this.cart.parts.vendorMinimumDollarAmount.replace(/[^0-9.-]+/g, ''))
        );
      }

      return null;
    },

    minDollarAmountPresent() {
      // When vendor min amount is set to null, backend sends a string amount of "0.0"
      // instead of null which causes the null logic checker on FE to be bypassed.
      // Therefore we need this new checker
      if (
        this.cart &&
        this.cart.parts &&
        this.cart.parts.price &&
        this.cart.parts.vendorMinimumDollarAmount &&
        this.cart.parts.isVendorMinimumDollarAmountNull === false
      ) {
        return true;
      }
      return false;
    },

    ...mapState('miniCart', ['bikes', 'parts', 'cart', 'items']),
    ...mapState('backend', ['isB2BPurchasable', 'encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  created() {
    this.updateMiniCart();
  },

  methods: {
    updateMiniCart: debounce(function() {
      this.fetchMiniCart();
    }, 1000),

    fullImageURL(item) {
      return (
        ACC.scene7ProductImageUrl +
        item.variantImage +
        '?wid=120&amp;hei=120&amp;fmt=png8-alpha&amp;qlt=80,1&amp;op_usm=0,0,0,0&amp;iccEmbed=0&amp;cache=on,on'
      );
    },

    checkoutDisabledCheck(event) {
      if (!this.isCheckoutEnabled) event.preventDefault();
    },

    ...mapActions('miniCart', ['fetchMiniCart']),
  },
};
</script>
